import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, TextField, Button, IconButton, Chip, Select, MenuItem,
  FormControl, InputLabel, CircularProgress, Alert, Divider, Avatar, Grid,
  Card, CardContent, FormControlLabel, Checkbox
} from '@mui/material';
import {
  Send as SendIcon, ArrowBack as BackIcon, Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDateDisplay } from '../../../utils/dateFormatter';
import supportService from '../../../services/supportService';

const STATUS_COLORS = { open: 'info', in_progress: 'warning', waiting_user: 'error', waiting_agent: 'default', resolved: 'success', closed: 'default' };
const STATUSES = ['open', 'in_progress', 'waiting_user', 'waiting_agent', 'resolved', 'closed'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const AdminSupportTicketDetail = () => {
  const { t } = useTranslation(['support', 'common']);
  const navigate = useNavigate();
  const { id } = useParams();
  const messagesEndRef = useRef(null);

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [agents, setAgents] = useState([]);
  const [cannedResponses, setCannedResponses] = useState([]);
  const [replyContent, setReplyContent] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const fetchTicket = useCallback(async () => {
    try {
      const res = await supportService.getAdminTicket(id);
      if (res.success) {
        setTicket(res.data);
        setMessages(res.data.messages || []);
      }
    } catch {
      setError(t('support:errors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await supportService.getAgents();
      if (res.success) setAgents(res.data || []);
    } catch { /* ignore */ }
  }, []);

  const fetchCannedResponses = useCallback(async () => {
    try {
      const res = await supportService.getCannedResponses();
      if (res.success) setCannedResponses(res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchTicket();
    fetchAgents();
    fetchCannedResponses();
  }, [fetchTicket, fetchAgents, fetchCannedResponses]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleStatusChange = async (status) => {
    try {
      await supportService.updateTicketStatus(id, status);
      fetchTicket();
    } catch { setError(t('support:errors.sendError')); }
  };

  const handlePriorityChange = async (priority) => {
    try {
      await supportService.updateTicketPriority(id, priority);
      fetchTicket();
    } catch { setError(t('support:errors.sendError')); }
  };

  const handleAssign = async (agentId) => {
    try {
      await supportService.assignAgent(id, agentId);
      fetchTicket();
    } catch { setError(t('support:errors.sendError')); }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('content', replyContent);
      formData.append('isInternalNote', isInternalNote);
      const res = await supportService.addAdminReply(id, formData);
      if (res.success) {
        setMessages(prev => [...prev, res.data]);
        setReplyContent('');
        setIsInternalNote(false);
      }
    } catch {
      setError(t('support:errors.sendError'));
    } finally {
      setSending(false);
    }
  };

  const handleUseCannedResponse = (content) => {
    setReplyContent(prev => prev ? `${prev}\n${content}` : content);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  if (!ticket) {
    return <Alert severity="error">{t('support:errors.loadError')}</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/admin/support/tickets')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {ticket.ticketNumber}
          </Typography>
          <Typography variant="h5" fontWeight={600}>{ticket.subject}</Typography>
        </Box>
        <IconButton onClick={fetchTicket}><RefreshIcon /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Messages column */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ maxHeight: '55vh', overflow: 'auto', mb: 2 }}>
            {messages.map((msg, idx) => {
              const isUser = msg.senderType === 'user';
              const isNote = msg.isInternalNote;
              return (
                <Box
                  key={msg.id || idx}
                  sx={{
                    p: 2,
                    bgcolor: isNote ? 'warning.50' : (isUser ? 'transparent' : 'action.hover'),
                    borderBottom: idx < messages.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                    borderLeft: isNote ? 3 : 0,
                    borderLeftColor: 'warning.main'
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: isUser ? 'primary.main' : 'secondary.main' }}>
                      {isUser ? (msg.sender?.name?.[0] || 'U') : 'A'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {msg.sender?.name || (isUser ? t('support:ticket.youSent') : t('support:ticket.agentReply'))}
                        </Typography>
                        {isNote && <Chip label={t('support:ticket.internalNote')} color="warning" size="small" />}
                        <Typography variant="caption" color="text.secondary">
                          {formatDateDisplay(msg.createdAt)}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                      {msg.attachments?.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {msg.attachments.map((att, i) => (
                            <Chip key={i} label={att.filename} size="small" component="a" href={att.url} target="_blank" clickable variant="outlined" />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </Paper>

          {/* Reply box */}
          <Paper sx={{ p: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder={isInternalNote ? t('support:admin.tickets.internalNotePlaceholder') : t('support:admin.tickets.reply')}
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <FormControlLabel
                control={<Checkbox checked={isInternalNote} onChange={(e) => setIsInternalNote(e.target.checked)} size="small" />}
                label={<Typography variant="body2">{t('support:admin.tickets.internalNote')}</Typography>}
              />
              <Button variant="contained" onClick={handleReply} disabled={sending || !replyContent.trim()} startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}>
                {t('support:admin.tickets.reply')}
              </Button>
            </Box>

            {/* Canned responses */}
            {cannedResponses.length > 0 && (
              <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">{t('support:admin.tickets.cannedResponses')}:</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {cannedResponses.map(cr => (
                    <Chip key={cr.id} label={cr.title} size="small" clickable onClick={() => handleUseCannedResponse(cr.content)} />
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>{t('support:admin.tickets.changeStatus')}</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <Select value={ticket.status} onChange={(e) => handleStatusChange(e.target.value)}>
                  {STATUSES.map(s => (
                    <MenuItem key={s} value={s}>{t(`support:status.${s}`)}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle2" gutterBottom>{t('support:admin.tickets.changePriority')}</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <Select value={ticket.priority} onChange={(e) => handlePriorityChange(e.target.value)}>
                  {PRIORITIES.map(p => (
                    <MenuItem key={p} value={p}>{t(`support:priority.${p}`)}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle2" gutterBottom>{t('support:admin.tickets.assignTo')}</Typography>
              <FormControl fullWidth size="small">
                <Select value={ticket.assignedAgentId || ''} onChange={(e) => handleAssign(e.target.value)}>
                  <MenuItem value="">{t('support:admin.tickets.unassigned')}</MenuItem>
                  {agents.map(a => (
                    <MenuItem key={a.id} value={a.id}>{a.user?.name || a.id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>{t('support:admin.tickets.ticketDetail')}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('support:admin.tickets.user')}</Typography>
                  <Typography variant="body2">{ticket.user?.name || ticket.contactEmail || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('support:ticket.category')}</Typography>
                  <Chip label={t(`support:categories.${ticket.category}`, ticket.category)} size="small" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('support:ticket.createdAt', { date: '' })}</Typography>
                  <Typography variant="body2">{formatDateDisplay(ticket.createdAt)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminSupportTicketDetail;
